import DOMPurify from 'dompurify';

// CSS constructs that can execute script or load active content. jsdom's
// DOMPurify lacks a full CSSOM, so its built-in style sanitizer is a no-op in
// tests; this hook makes the behaviour deterministic in both jsdom and the
// browser.
const DANGEROUS_CSS = /(?:javascript:|vbscript:|expression\s*\(|behavior\s*:|-moz-binding)/i;

// Remote (or protocol-relative) resource references. Rendering these in a
// preview would fire an OUTBOUND network request on open (tracking pixel that
// leaks reviewer IP / time-of-open, internal session contacting untrusted
// hosts), so they are neutralised. Embedded `cid:` (inline MIME part) and
// `data:` URIs are NOT remote and stay intact, so the preview still reflects the
// real inline content of the mail (RITA-1307 audit P1 cid: + P2 external loads).
const REMOTE_URL_VALUE = /^\s*(?:https?:)?\/\//i; // http://… , https://… , //host…
const REMOTE_CSS_URL = /url\(\s*['"]?\s*(?:https?:)?\/\//i; // url(http(s)://…) / url(//…)
// `srcset` is a candidate LIST (`url descriptor, url descriptor, …`), so a
// remote URL can hide after a safe first entry (e.g.
// `srcset="/logo.png 1x, //tracker/pixel 2x"`). Matching only the whole value
// (REMOTE_URL_VALUE) would miss that and the browser would still fetch the
// remote candidate (notably on high-DPR screens). A candidate URL always starts
// the string or follows a comma, so flag a remote load whenever `http(s)://` or
// `//` appears at a candidate boundary. `cid:`/relative candidates never match
// (a comma inside e.g. a query string is followed by normal chars, not `//`), so
// they survive. (`data:` candidates are dropped by DOMPurify's own srcset
// handling regardless — harmless, since data: makes no network request.)
const REMOTE_URL_IN_SRCSET = /(?:^|,)\s*(?:https?:)?\/\//i;

// Attributes that make the browser fetch a remote resource on render. `href` is
// deliberately NOT here: on a hyperlink it only fetches on click, so external
// links stay (see HREF_ATTRS below for the SVG exception).
const RESOURCE_ATTRS = new Set(['src', 'srcset', 'poster', 'background', 'lowsrc']);

// `href` / `xlink:href` are auto-loading on SVG resource elements (`<image>`,
// `<use>`, `<feImage>`, `<pattern>`, …): opening a preview would fire the same
// outbound request as an `<img src>` (tracking pixel, IP/time-of-open leak). Only
// real hyperlinks (`<a>`, `<area>`) fetch on click instead of on render, so href
// stays there. We deny-by-default on every other tag rather than enumerate the
// SVG elements — that way a not-yet-listed SVG tag can never reopen the hole.
const HREF_ATTRS = new Set(['href', 'xlink:href']);
const HYPERLINK_TAGS = new Set(['a', 'area']);

let hooksRegistered = false;
function ensureHooks(): void {
  if (hooksRegistered) return;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    /* eslint-disable no-param-reassign -- DOMPurify hooks mutate `data` by contract. */
    // Inline `style`: keep harmless formatting (color, font, alignment, …),
    // strip dangerous declarations and any declaration loading a remote resource
    // via url(...). Legacy/Outlook/Word mail relies on inline CSS, so we keep the
    // attribute itself unless nothing safe is left.
    if (data.attrName === 'style' && data.attrValue) {
      const cleaned = data.attrValue
        .split(';')
        .map((decl) => decl.trim())
        .filter((decl) => decl.length > 0 && !DANGEROUS_CSS.test(decl) && !REMOTE_CSS_URL.test(decl))
        .join('; ');
      if (cleaned) {
        data.attrValue = cleaned;
      } else {
        data.keepAttr = false;
      }
      return;
    }
    // Resource-loading attributes: drop the attribute when it points at a remote
    // (or protocol-relative) URL so opening a preview never triggers a network
    // request. `cid:`/`data:`/relative references are kept. `srcset` is a
    // candidate list, so it is checked per-candidate (REMOTE_URL_IN_SRCSET);
    // single-URL attributes use the whole-value check.
    if (RESOURCE_ATTRS.has(data.attrName.toLowerCase()) && data.attrValue) {
      const isSrcset = data.attrName.toLowerCase() === 'srcset';
      const remote = isSrcset ? REMOTE_URL_IN_SRCSET.test(data.attrValue) : REMOTE_URL_VALUE.test(data.attrValue);
      if (remote) {
        data.keepAttr = false;
      }
    }
    // `href` / `xlink:href`: on SVG resource elements (e.g. <image>, <use>,
    // <feImage>) these load on render, exactly like `src`. Drop them when remote,
    // EXCEPT on hyperlinks (<a>/<area>) which only fetch on click. `cid:`/`data:`/
    // relative references are kept, consistent with the resource-attr policy above.
    if (HREF_ATTRS.has(data.attrName.toLowerCase()) && data.attrValue) {
      const tag = (node.nodeName || '').toLowerCase();
      if (!HYPERLINK_TAGS.has(tag) && REMOTE_URL_VALUE.test(data.attrValue)) {
        data.keepAttr = false;
      }
    }
    /* eslint-enable no-param-reassign */
  });
  hooksRegistered = true;
}

/**
 * Sanitizes stored e-mail HTML before it is rendered as raw markup in the UI.
 *
 * Background (RITA-1307): system access mails can be edited by internal staff
 * before sending, so the stored `mail_content` / `mail_content_snapshot` is no
 * longer guaranteed to be admin-authored template HTML. Rendering it unsanitized
 * is a stored-XSS risk. We keep formatting (bold, lists, links, inline styling, …)
 * but strip any active or dangerous content (scripts, event handlers,
 * javascript: URLs, dangerous CSS, …).
 *
 * Two deliberate rules for embedded vs. remote content:
 *  - `cid:` inline-image references are KEPT — they are part of the real mail and
 *    make no network request, so the preview/diff stays faithful to the message.
 *  - Remote resource loads (`http(s)`/protocol-relative `img src`, CSS `url(...)`,
 *    and SVG `href`/`xlink:href` on resource elements like `<image>`/`<use>`) are
 *    STRIPPED so merely opening a preview never contacts an external host. Normal
 *    hyperlinks (`<a href>`/`<area href>`) are untouched — they only fetch on click.
 */
export default function sanitizeEmailHtml(html: string | null | undefined): string {
  if (!html) return '';
  ensureHooks();
  return DOMPurify.sanitize(html, {
    // Drop script/style-blocks/etc. entirely instead of leaving their text
    // content. (The `style` TAG is forbidden; inline `style` ATTRIBUTES are
    // allowed and CSS-sanitized by the hook above.)
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
    // Allow normal mail link schemes plus `cid:` (embedded inline images).
    // `https:` stays allowed so genuine links (href) survive; remote resource
    // LOADING is blocked by the hook above, not here.
    //
    // `data:` is deliberately NOT in this allowlist, yet embedded `data:image/…`
    // sources survive — DOMPurify keeps `data:` URIs on its built-in
    // `DATA_URI_TAGS` (img, image, source, video, audio, track) via a separate
    // code path that is independent of `ALLOWED_URI_REGEXP`. Routing inline
    // images through that path (instead of widening this regex) is intentional:
    // it keeps `data:image/…` on real media elements (preview stays faithful, no
    // network request) while `data:text/html` / `data:` on a hyperlink stays
    // blocked — adding `data:` here would open exactly that XSS surface. Covered
    // by the "keeps data: image sources" + "blocks data:text/html on a link"
    // tests. (RITA-1307 review false-positive: the regex alone looks like it
    // strips data:, but DATA_URI_TAGS keeps the safe subset.)
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}
